import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCampDisplayName } from '@/utils/campNameFormatter';
import { 
  Heart, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Users, 
  Star,
  ExternalLink,
  Phone,
  Mail,
  Globe,
  Navigation,
  Edit,
  Trash2,
  X
} from 'lucide-react';

/**
 * Helper function to find child by name and return photo URL
 * Handles various name formats and matching scenarios, including ID matching
 */
const findChildPhoto = (childName, children = []) => {
  if (!childName || !children || children.length === 0) return null;
  
  // Normalize the input name (trim, lowercase, remove extra spaces)
  const normalize = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
  };
  
  const normalizedInput = normalize(childName);
  if (!normalizedInput) return null;
  
  // First, try matching by ID (in case childName is actually a child ID from old data)
  let child = children.find(c => {
    if (!c) return false;
    // Check if childName matches the child's ID
    if (c.id && (c.id === childName || c.id.toString() === childName.toString())) {
      return true;
    }
    return false;
  });
  
  // If no ID match, try exact name match
  if (!child) {
    child = children.find(c => {
      if (!c || !c.name) return false;
      const normalizedChildName = normalize(c.name);
      return normalizedChildName === normalizedInput;
    });
  }
  
  // If no exact match, try partial matching (first name only)
  if (!child && normalizedInput.includes(' ')) {
    const firstName = normalizedInput.split(' ')[0];
    child = children.find(c => {
      if (!c || !c.name) return false;
      const normalizedChildName = normalize(c.name);
      return normalizedChildName.split(' ')[0] === firstName || 
             normalizedChildName === firstName;
    });
  }
  
  // Return photo URL if found
  return child?.photo_url || null;
};

/**
 * Camp Card Component for Grid View
 */
const CampGridCard = ({ 
  camp, 
  onFavorite, 
  onEdit, 
  onDelete, 
  onDirections,
  isFavorite = false,
  isLoading = false,
  children = [] // Array of child objects with name and photo_url
}) => {
  // Get source badge styling
  const getSourceBadge = () => {
    const sourceMap = {
      'ai_discovered': { label: 'AI DISCOVERED', color: 'bg-blue-100 text-blue-800' },
      'search': { label: 'SEARCH RESULT', color: 'bg-green-100 text-green-800' }
    };
    
    // Only show badge for AI discovered or search result camps
    if (!camp.source || camp.source === 'manual_entry' || camp.source === 'manual') {
      return null;
    }
    
    const source = sourceMap[camp.source];
    if (!source) return null;
    
    return (
      <Badge className={`text-xs font-medium ${source.color}`}>
        {source.label}
      </Badge>
    );
  };

  // Get status badges
  const getStatusBadges = () => {
    if (!camp.status || camp.status.length === 0) return null;
    
    const statusMap = {
      favorite: { label: 'Favorite', color: 'bg-purple-100 text-brand-800' },
      booked: { label: 'Booked', color: 'bg-green-100 text-green-800' },
      considering: { label: 'Considering', color: 'bg-yellow-100 text-yellow-800' },
      attended: { label: 'Attended', color: 'bg-blue-100 text-blue-800' },
      saved: { label: 'Saved', color: 'bg-orange-100 text-orange-800' },
      recommended: { label: 'Recommended', color: 'bg-pink-100 text-pink-800' }
    };

    return (
      <div className="flex flex-wrap gap-1">
        {camp.status
          .filter(status => status !== 'saved') // Filter out 'saved' status since all camps are saved
          .map((status) => {
            const statusInfo = statusMap[status];
            if (!statusInfo) return null;
            
            return (
              <Badge key={status} className={`text-xs ${statusInfo.color}`}>
                {statusInfo.label}
              </Badge>
            );
          })}
      </div>
    );
  };

  // Format price display
  const formatPrice = () => {
    // If camp is booked, always show the booking price (what user actually paid)
    const isBooked = camp.status && camp.status.includes('booked');
    const hasBookingPrice = camp.regularPrice && isBooked;
    
    // Debug logging for price display
    if (camp.name === 'Camp Invention') {
      console.log('Camp Invention Price Debug:', {
        isBooked,
        hasBookingPrice,
        regularPrice: camp.regularPrice,
        earlyBirdPrice: camp.earlyBirdPrice,
        status: camp.status
      });
    }
    
    if (hasBookingPrice) {
      // Just show booking price - no crossed out price
      return (
        <div className="text-lg font-bold text-gray-900">${camp.regularPrice}</div>
      );
    }
    
    // Not booked - show regular pricing logic
    if (camp.earlyBirdPrice && camp.regularPrice && camp.earlyBirdPrice < camp.regularPrice) {
      return (
        <div className="space-y-1">
          <div className="text-lg font-bold text-gray-900">${camp.earlyBirdPrice}</div>
          <div className="text-sm text-gray-500 line-through">${camp.regularPrice}</div>
          <div className="text-xs text-green-600 font-medium">
            Save ${camp.regularPrice - camp.earlyBirdPrice}
          </div>
        </div>
      );
    }
    
    if (camp.regularPrice) {
      return (
        <div className="text-lg font-bold text-gray-900">${camp.regularPrice}</div>
      );
    }
    
    return null;
  };

  // Format date range
  const formatDateRange = () => {
    if (!camp.campStartDate) return 'Dates TBD';
    
    // Debug logging for dates
    if (camp.name === 'Camp Invention') {
      console.log('CampCard - Camp Invention Date Debug:', {
        campStartDate: camp.campStartDate,
        campEndDate: camp.campEndDate,
        startDateType: typeof camp.campStartDate,
        endDateType: typeof camp.campEndDate
      });
    }
    
    // Handle date parsing to avoid timezone issues
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      
      // If it's already a Date object, return it
      if (dateStr instanceof Date) return dateStr;
      
      // If it's an ISO date string (YYYY-MM-DD), parse it as local date to avoid timezone issues
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      }
      
      // Fallback to regular Date parsing
      return new Date(dateStr);
    };
    
    const start = parseDate(camp.campStartDate);
    const end = camp.campEndDate ? parseDate(camp.campEndDate) : null;
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
    };

    if (end && start.getTime() !== end.getTime()) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    
    return formatDate(start);
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <div className="h-40 bg-gray-200"></div>
        <CardContent className="p-4 space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden">
      {/* Header with photo/emoji and badges */}
      <div className="relative h-40">
        {camp.image_url ? (
          <>
            <img 
              src={camp.image_url} 
              alt={formatCampDisplayName(camp)}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide broken image and show emoji fallback
                e.target.style.display = 'none';
                const emojiDiv = e.target.nextElementSibling;
                if (emojiDiv) emojiDiv.style.display = 'flex';
              }}
            />
            {/* Emoji fallback - hidden until image fails */}
            <div 
              className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center"
              style={{ display: 'none' }}
            >
              <div className="text-6xl">
                {camp.emoji || '🏕️'}
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
            <div className="text-6xl">
              {camp.emoji || '🏕️'}
            </div>
          </div>
        )}
        
        {/* Source Badge */}
        <div className="absolute top-3 left-3 z-10">
          {getSourceBadge()}
        </div>
        
        {/* Heart Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onFavorite(camp)}
          className={`
            absolute top-3 right-3 z-10 rounded-full backdrop-blur-sm transition-all duration-200
            ${isFavorite 
              ? "bg-red-500/90 text-white hover:bg-red-600/90" 
              : "bg-white/90 text-gray-600 hover:bg-white hover:text-red-500"
            }
          `}
        >
          <Heart className={`h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
        </Button>
      </div>

      <CardContent className="p-4 space-y-3">
        {/* Title and Rating */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-bold text-gray-900 leading-tight line-clamp-2">
              {formatCampDisplayName(camp)}
            </h3>
            {camp.rating && (
              <div className="flex items-center gap-1 text-sm font-medium text-gray-600 flex-shrink-0">
                <Star className="h-4 w-4 fill-current text-yellow-400" />
                {camp.rating}
              </div>
            )}
          </div>
          
          {camp.organization && camp.organization.trim().toLowerCase() !== 'unknown organization' && (
            <p className="text-sm text-gray-600 line-clamp-1">
              {camp.organization}
            </p>
          )}
        </div>

        {/* Details */}
        <div className="space-y-2 text-sm text-gray-600">
          {camp.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="line-clamp-1">{camp.location}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span>{formatDateRange()}</span>
          </div>
          
          {camp.ageRange && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 flex-shrink-0" />
              <span>{camp.ageRange}</span>
            </div>
          )}
          {camp.grade_range && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 flex-shrink-0" />
              <span>{camp.grade_range}</span>
            </div>
          )}
        </div>

        {/* Assigned Children with Profile Circles */}
        {camp.assignedChildren && camp.assignedChildren.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {camp.assignedChildren
                .filter(childName => {
                  // Filter out invalid child names
                  if (!childName || typeof childName !== 'string') return false;
                  const trimmed = childName.trim();
                  return trimmed.length > 0 && 
                         trimmed.toLowerCase() !== 'child' && 
                         trimmed.toLowerCase() !== 'children';
                })
                .map((childName, idx) => {
                // Get initials from child name
                const getInitials = (name) => {
                  // Handle null, undefined, or empty strings
                  if (!name || typeof name !== 'string' || name.trim().length === 0) {
                    return '??'; // Default fallback
                  }
                  
                  const trimmed = name.trim();
                  // Filter out generic names
                  if (trimmed.toLowerCase() === 'child' || trimmed.toLowerCase() === 'children') {
                    return '??';
                  }
                  
                  return trimmed
                    .split(' ')
                    .filter(n => n.length > 0) // Filter out empty strings
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || '??'; // Fallback if result is empty
                };
                
                // Generate a consistent color based on the child's name
                const getColorClass = (name) => {
                  const colors = [
                    'bg-purple-500',
                    'bg-blue-500',
                    'bg-green-500',
                    'bg-yellow-500',
                    'bg-pink-500',
                    'bg-indigo-500',
                    'bg-teal-500',
                    'bg-orange-500'
                  ];
                  
                  // Handle null, undefined, or empty strings
                  if (!name || typeof name !== 'string' || name.trim().length === 0) {
                    return colors[0]; // Default to first color
                  }
                  
                  const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
                  return colors[index];
                };
                
                // Try to find photo URL for this child
                const photoUrl = findChildPhoto(childName, children);
                
                // Debug logging (remove in production if needed)
                if (process.env.NODE_ENV === 'development' && children.length > 0 && !photoUrl) {
                  console.log('No photo found for child:', {
                    childName,
                    childrenNames: children.map(c => c?.name),
                    children: children.map(c => ({ name: c?.name, photo_url: c?.photo_url }))
                  });
                }
                
                return (
                  <div
                    key={idx}
                    className="relative w-8 h-8 rounded-full border-2 border-white shadow-sm overflow-hidden"
                    title={childName}
                  >
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={childName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = `w-8 h-8 rounded-full ${getColorClass(childName)} flex items-center justify-center text-white text-xs font-semibold`;
                            fallback.textContent = getInitials(childName);
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <div className={`w-8 h-8 rounded-full ${getColorClass(childName)} flex items-center justify-center text-white text-xs font-semibold`}>
                        {getInitials(childName)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <span className="text-xs text-gray-600 font-medium">
              {camp.assignedChildren.length === 1 
                ? camp.assignedChildren[0]
                : `${camp.assignedChildren.length} children`
              }
            </span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-gray-500" />
            {formatPrice()}
          </div>
        </div>

        {/* Status Tags */}
        {getStatusBadges()}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(camp)}
            className="flex-1 text-xs border-purple-200 text-brand-600 hover:bg-purple-50"
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(camp)}
            className="flex-1 text-xs border-red-200 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDirections(camp)}
            className="flex-1 text-xs"
          >
            <Navigation className="h-3 w-3 mr-1" />
            Directions
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Camp List Item Component for List View
 * Memoized to prevent unnecessary re-renders
 */
const CampListItem = React.memo(({ 
  camp, 
  onFavorite, 
  onEdit, 
  onDelete, 
  onDirections,
  onCancelBooking,
  isFavorite = false,
  isLoading = false,
  children = [] // Array of child objects with name and photo_url
}) => {
  // Get source badge styling
  const getSourceBadge = () => {
    const sourceMap = {
      'ai_discovered': { label: 'AI DISCOVERED', color: 'bg-blue-100 text-blue-800' },
      'search': { label: 'SEARCH RESULT', color: 'bg-green-100 text-green-800' }
    };
    
    // Only show badge for AI discovered or search result camps
    if (!camp.source || camp.source === 'manual_entry' || camp.source === 'manual') {
      return null;
    }
    
    const source = sourceMap[camp.source];
    if (!source) return null;
    
    return (
      <Badge className={`text-xs font-medium ${source.color}`}>
        {source.label}
      </Badge>
    );
  };

  // Get status badges
  const getStatusBadges = () => {
    if (!camp.status || camp.status.length === 0) return null;
    
    const statusMap = {
      favorite: { label: 'Favorite', color: 'bg-purple-100 text-brand-800' },
      booked: { label: 'Booked', color: 'bg-green-100 text-green-800' },
      considering: { label: 'Considering', color: 'bg-yellow-100 text-yellow-800' },
      attended: { label: 'Attended', color: 'bg-blue-100 text-blue-800' },
      saved: { label: 'Saved', color: 'bg-orange-100 text-orange-800' },
      recommended: { label: 'Recommended', color: 'bg-pink-100 text-pink-800' }
    };

    return (
      <div className="flex flex-wrap gap-1">
        {camp.status
          .filter(status => status !== 'saved') // Filter out 'saved' status since all camps are saved
          .map((status) => {
            const statusInfo = statusMap[status];
            if (!statusInfo) return null;
            
            return (
              <Badge key={status} className={`text-xs ${statusInfo.color}`}>
                {statusInfo.label}
              </Badge>
            );
          })}
      </div>
    );
  };

  // Format price display
  const formatPrice = () => {
    // If camp is booked, always show the booking price (what user actually paid)
    const isBooked = camp.status && camp.status.includes('booked');
    const hasBookingPrice = camp.regularPrice && isBooked;
    
    if (hasBookingPrice) {
      // Just show booking price - no crossed out price
      return (
        <div className="text-lg font-bold text-gray-900">${camp.regularPrice}</div>
      );
    }
    
    // Not booked - show regular pricing logic
    if (camp.earlyBirdPrice && camp.regularPrice && camp.earlyBirdPrice < camp.regularPrice) {
      return (
        <div className="text-right">
          <div className="text-lg font-bold text-gray-900">${camp.earlyBirdPrice}</div>
          <div className="text-sm text-gray-500 line-through">${camp.regularPrice}</div>
        </div>
      );
    }
    
    if (camp.regularPrice) {
      return (
        <div className="text-lg font-bold text-gray-900">${camp.regularPrice}</div>
      );
    }
    
    return null;
  };

  // Format date range
  const formatDateRange = () => {
    if (!camp.campStartDate) return 'Dates TBD';
    
    // Debug logging for dates
    if (camp.name === 'Camp Invention') {
      console.log('CampCard - Camp Invention Date Debug:', {
        campStartDate: camp.campStartDate,
        campEndDate: camp.campEndDate,
        startDateType: typeof camp.campStartDate,
        endDateType: typeof camp.campEndDate
      });
    }
    
    // Handle date parsing to avoid timezone issues
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      
      // If it's already a Date object, return it
      if (dateStr instanceof Date) return dateStr;
      
      // If it's an ISO date string (YYYY-MM-DD), parse it as local date to avoid timezone issues
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      }
      
      // Fallback to regular Date parsing
      return new Date(dateStr);
    };
    
    const start = parseDate(camp.campStartDate);
    const end = camp.campEndDate ? parseDate(camp.campEndDate) : null;
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
    };

    if (end && start.getTime() !== end.getTime()) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    
    return formatDate(start);
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-gray-200 rounded-lg"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
            <div className="w-16 h-8 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="group hover:shadow-md transition-all duration-300 border border-gray-100">
      <CardContent className="p-3 md:p-4">
        <div className="flex items-start gap-3">
          {/* Camp Icon/Photo */}
          <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden relative">
            {camp.image_url ? (
              <>
                <img 
                  src={camp.image_url} 
                  alt={formatCampDisplayName(camp)}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Hide broken image and show emoji fallback
                    e.target.style.display = 'none';
                    const emojiDiv = e.target.nextElementSibling;
                    if (emojiDiv) emojiDiv.style.display = 'flex';
                  }}
                />
                {/* Emoji fallback - hidden until image fails */}
                <div 
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ display: 'none' }}
                >
                  <div className="text-xl md:text-2xl">
                    {camp.emoji || '🏕️'}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-xl md:text-2xl">
                {camp.emoji || '🏕️'}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header: Name, Badges, Rating with Top Right Info */}
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-bold text-gray-900 truncate text-sm md:text-base">
                    {formatCampDisplayName(camp)}
                  </h3>
                  {getSourceBadge()}
                  {camp.rating && (
                    <div className="flex items-center gap-0.5 text-xs md:text-sm font-medium text-gray-600 flex-shrink-0">
                      <Star className="h-3 w-3 fill-current text-yellow-400" />
                      {camp.rating}
                    </div>
                  )}
                </div>
                
                {camp.organization && camp.organization.trim().toLowerCase() !== 'unknown organization' && (
                  <p className="text-xs md:text-sm text-gray-600 truncate mb-1">
                    {camp.organization}
                  </p>
                )}
              </div>
              
              {/* Top Right: Children, Date, Price */}
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {/* Assigned Children with Profile Circles */}
                {camp.assignedChildren && camp.assignedChildren.length > 0 && (
                  <div className="flex items-center gap-1.5 justify-end">
                    <div className="flex -space-x-1.5">
                      {camp.assignedChildren
                        .filter(childName => {
                          // Filter out invalid child names
                          if (!childName || typeof childName !== 'string') return false;
                          const trimmed = childName.trim();
                          return trimmed.length > 0 && 
                                 trimmed.toLowerCase() !== 'child' && 
                                 trimmed.toLowerCase() !== 'children';
                        })
                        .map((childName, idx) => {
                          // Get initials from child name
                          const getInitials = (name) => {
                            // Handle null, undefined, or empty strings
                            if (!name || typeof name !== 'string' || name.trim().length === 0) {
                              return '??'; // Default fallback
                            }
                            
                            const trimmed = name.trim();
                            // Filter out generic names
                            if (trimmed.toLowerCase() === 'child' || trimmed.toLowerCase() === 'children') {
                              return '??';
                            }
                            
                            return trimmed
                              .split(' ')
                              .filter(n => n.length > 0) // Filter out empty strings
                              .map(n => n[0])
                              .join('')
                              .toUpperCase()
                              .slice(0, 2) || '??'; // Fallback if result is empty
                          };
                          
                          // Generate a consistent color based on the child's name
                          const getColorClass = (name) => {
                            const colors = [
                              'bg-purple-500',
                              'bg-blue-500',
                              'bg-green-500',
                              'bg-yellow-500',
                              'bg-pink-500',
                              'bg-indigo-500',
                              'bg-teal-500',
                              'bg-orange-500'
                            ];
                            
                            // Handle null, undefined, or empty strings
                            if (!name || typeof name !== 'string' || name.trim().length === 0) {
                              return colors[0]; // Default to first color
                            }
                            
                            const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
                            return colors[index];
                          };
                          
                          // Try to find photo URL for this child
                          const photoUrl = findChildPhoto(childName, children);
                          
                          return (
                            <div
                              key={idx}
                              className="relative w-6 h-6 md:w-7 md:h-7 rounded-full border-2 border-white shadow-sm overflow-hidden"
                              title={childName}
                            >
                              {photoUrl ? (
                                <img
                                  src={photoUrl}
                                  alt={childName}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Fallback to initials if image fails to load
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    if (parent) {
                                      const fallback = document.createElement('div');
                                      fallback.className = `w-6 h-6 md:w-7 md:h-7 rounded-full ${getColorClass(childName)} flex items-center justify-center text-white text-[10px] md:text-xs font-semibold`;
                                      fallback.textContent = getInitials(childName);
                                      parent.appendChild(fallback);
                                    }
                                  }}
                                />
                              ) : (
                                <div className={`w-6 h-6 md:w-7 md:h-7 rounded-full ${getColorClass(childName)} flex items-center justify-center text-white text-[10px] md:text-xs font-semibold`}>
                                  {getInitials(childName)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                
                {/* Date - compact display */}
                {camp.campStartDate && (
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDateRange()}</span>
                  </div>
                )}
                
                {/* Price - only show if price exists */}
                {(() => {
                  const priceDisplay = formatPrice();
                  return priceDisplay && (
                    <div className="text-right">
                      {priceDisplay}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Details: Location, Age - compact horizontal layout */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm text-gray-600 mb-1.5">
              {camp.location && (
                <div className="flex items-center gap-1 min-w-0 max-w-[calc(100%-4rem)]">
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{camp.location}</span>
                </div>
              )}
              
              {camp.ageRange && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Users className="h-3 w-3" />
                  <span>{camp.ageRange}</span>
                </div>
              )}
              {camp.grade_range && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Users className="h-3 w-3" />
                  <span>{camp.grade_range}</span>
                </div>
              )}
            </div>

            {/* Status Tags and Action Buttons - combined in one row */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Status Tags */}
              {getStatusBadges()}
              
              {/* Action Buttons */}
              <div className="flex items-center gap-1.5 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(camp)}
                  className="h-7 md:h-8 px-2 md:px-3 text-xs border-purple-200 text-brand-600 hover:bg-purple-50"
                >
                  <Edit className="h-3 w-3 md:mr-1" />
                  <span className="hidden md:inline">Edit</span>
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(camp)}
                  className="h-7 md:h-8 px-2 md:px-3 text-xs border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3 md:mr-1" />
                  <span className="hidden md:inline">Delete</span>
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDirections(camp)}
                  className="h-7 md:h-8 px-2 md:px-3 text-xs"
                >
                  <Navigation className="h-3 w-3 md:mr-1" />
                  <span className="hidden md:inline">Directions</span>
                </Button>
                
                {/* Cancel Booking Button - only show for booked camps */}
                {(() => {
                  const isBooked = camp.status?.includes('booked');
                  const hasCancelHandler = !!onCancelBooking;
                  return isBooked && hasCancelHandler;
                })() && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCancelBooking(camp)}
                    className="h-7 md:h-8 px-2 md:px-3 text-xs border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <X className="h-3 w-3 md:mr-1" />
                    <span className="hidden md:inline">Cancel</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return (
    prevProps.camp?.id === nextProps.camp?.id &&
    prevProps.isFavorite === nextProps.isFavorite &&
    prevProps.isLoading === nextProps.isLoading &&
    JSON.stringify(prevProps.camp?.status) === JSON.stringify(nextProps.camp?.status)
  );
});

CampListItem.displayName = 'CampListItem';

export { CampGridCard, CampListItem };
